import React from "react";
import Icon from "./Icon";
import ApplicantRow, { Avatar } from "./ApplicantRow";
import { formatSessionTimeRange } from "../../studentSessionHelpers";
import { roleLabel, sessionLabel, daysUntil, isUrgent, earliestPending, agoLabel, formatDay, orderApplicants } from "./signupsModel";

const DAY = 864e5;
const catLabel = (c) => (c ? String(c).charAt(0).toUpperCase() + String(c).slice(1) : "Other");

function Urgency({ group, now }) {
  const d = daysUntil(group.sessionDate, now);
  if (isUrgent(group, now)) {
    return (
      <div className="isg-urg">
        <span className="isg-st isg-st-urg"><Icon name="alertC" size={12} />Starts in {d} {d === 1 ? "day" : "days"}</span>
        <small>No instructor approved</small>
      </div>
    );
  }
  return (
    <div className="isg-urg">
      <span className="isg-st isg-st-soon">In {d} {d === 1 ? "day" : "days"}</span>
      <small>No instructor approved</small>
    </div>
  );
}

function FilledBy({ group, filler, now, flight, onUndo }) {
  const when = group.filledAt
    ? (now - new Date(group.filledAt).getTime() < DAY ? `${agoLabel(group.filledAt, now)} ago` : formatDay(String(group.filledAt).slice(0, 10)))
    : "";
  return (
    <div className="isg-filled">
      <Avatar name={filler?.instructorName} />
      <div className="t">
        <b>Filled by {filler?.instructorName || "an instructor"}</b>
        <small>{flight ? "Undoing…" : when ? `Approved ${when}` : ""}</small>
      </div>
      <button type="button" className="isg-btn isg-btn-o isg-btn-sm" disabled={Boolean(flight)} onClick={onUndo}
        aria-label={`Undo: unapprove ${filler?.instructorName || "instructor"}`}>
        <Icon name="undo" size={13} />Undo
      </button>
    </div>
  );
}

/** One slot (opening) with its applicants. Filled slots collapse to a "Filled by" summary with Undo. */
export default function SlotGroup({ group, ui, now, onToggle, onApprove, onUnapprove, onGo, onCancel, onDismiss, onUndo, onNote, onRejectGo }) {
  const filler = group.status === "FILLED" ? group.applicants.find((a) => a.instructorId === group.assignedInstructorId) : null;
  const filled = group.status === "FILLED";
  const open = ui.open ?? !filled;
  const waiting = earliestPending(group);
  const headingId = `isg-gh-${group.openingId}`;
  const otherPending = filled ? group.applicants.filter((a) => a.status === "PENDING").length : 0;
  const cat = group.courseCategory ? String(group.courseCategory).toLowerCase() : "other";

  return (
    <section className={`isg-grp${open ? " open" : ""}${filled ? " done" : ""}`} aria-labelledby={headingId}
      onKeyDown={(e) => { if (e.key === "Escape" && (ui.confirm || ui.reject)) { e.preventDefault(); onCancel(); } }}>
      <div className="isg-g-h">
        <button type="button" className="isg-ib isg-tg" aria-expanded={open}
          aria-label={`${open ? "Collapse" : "Expand"} applicants`} onClick={onToggle}>
          <Icon name="chev" size={16} />
        </button>
        <span className="isg-tile" style={{ "--c": `var(--color-cat-${cat}, var(--color-cat-other))` }} aria-hidden="true"><Icon name="cap" size={18} /></span>
        <div>
          <div className="isg-g-n">
            <b id={headingId}>{group.courseTitle}</b>
            <span className="isg-mono">{group.courseNumber}</span>
            <span className="isg-cat" style={{ "--c": `var(--color-cat-${cat}, var(--color-cat-other))` }}><i />{catLabel(group.courseCategory)}</span>
          </div>
          <div className="isg-g-m">
            <span className="isg-d"><Icon name="cal" size={13} />{formatDay(group.sessionDate)} · {formatSessionTimeRange(group)}</span>
            {group.location && <span><Icon name="pin" size={13} />{group.location}</span>}
          </div>
        </div>
        <div className="isg-g-r"><span className="isg-k">Role needed</span><b style={{ fontWeight: 600 }}>{roleLabel(group.teachingRole)}</b></div>
        <div className="isg-g-k isg-g-a">
          <span className="isg-k">Applicants</span><b>{group.applicants.length}</b>
          <span className="isg-stk">{group.applicants.slice(0, 4).map((a) => <Avatar key={a.id} name={a.instructorName} size="xs" />)}</span>
        </div>
        <div className="isg-g-k isg-g-w"><span className="isg-k">Waiting</span><b>{waiting && !filled ? agoLabel(waiting, now) : "—"}</b></div>
        <div className="isg-g-s">
          {filled
            ? <FilledBy group={group} filler={filler} now={now} flight={Boolean(ui.flight)} onUndo={() => filler && onUndo(filler)} />
            : <Urgency group={group} now={now} />}
        </div>
      </div>
      {open ? (
        <div className="isg-apps">
          <div className="isg-ah" aria-hidden="true"><span>Instructor</span><span>Eligibility</span><span>Status</span><span /></div>
          <div role="list" aria-label={`Applicants for ${group.courseTitle}, ${sessionLabel(group)}`}>
            {orderApplicants(group).map((a) => (
              <ApplicantRow key={a.id} applicant={a} group={group} ui={ui} now={now}
                onApprove={onApprove} onUnapprove={onUnapprove} onGo={onGo} onCancel={onCancel} onDismiss={onDismiss}
                onNote={onNote} onRejectGo={onRejectGo} />
            ))}
          </div>
        </div>
      ) : filled && otherPending > 0 ? (
        <div className="isg-note">
          {otherPending} other applicant{otherPending > 1 ? "s stay" : " stays"} pending. Approve is disabled while this slot is filled.
          <button type="button" onClick={onToggle}>Show applicants</button>
        </div>
      ) : null}
    </section>
  );
}
